import useResizeObserver from "@react-hook/resize-observer";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueries, useQuery } from "react-query";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { useShallow } from "zustand/react/shallow";

import { Feature, MultiPolygon, Polygon, Position } from "@turf/helpers";
import * as turf from "@turf/turf";
import { Alert } from "antd";
import axios, { AxiosResponse } from "axios";
import colormap from "colormap";
import _debounce from "lodash/debounce";
import { RootState } from "store/rootReducer";
import {
  selectAttentionWells,
  selectIsXdaLineReversed,
  selectSelectedWells,
  selectTxnIdId,
  selectXdaLineRequest,
  useMapStore
} from "stores/map";
import type { ISelectedWell } from "stores/map";
import styled from "styled-components";
import Two from "two.js";
import ZUI from "two.js/extras/jsm/zui.js";
import { getP10P90Percentile } from "utils";
import { calculateTextDimensions } from "utils/dom";
import { getMaxDecimalPlaces } from "utils/getMaxDecimalPlaces";
import { getXdaPayloadWells, isGroupModeSelection } from "utils/groupModeHelpers";

import { useLengthConversion } from "hooks";

import { getDynamicBinSettings } from "api/getDynamicBinSettings";
import {
  ENTITIES_ON_MAP_QUERY_KEY,
  ENTITIES_ON_MAP_STALE_TIME,
  MAP_QUERY_CACHE_TIME,
  MapEntity,
  fetchEntitiesOnMap
} from "api/map";

import { LegendItemModel, MutationParameters, xdaSessionKey } from "models";
import Uwi from "models/uwi";
import { ScreenSize, XdaData, XdaLayoutOptions } from "models/xdaData";

import { BusyOverlay, ErrorOverlay } from "components/activity/shared";
import { useDashboardContext, useDashboardDispatch } from "components/dashboard/hooks";
import { IconSpinner } from "components/icons";
import { useIpdbStore } from "components/map/contexts/IpdbStoreProvider";
import type { EntitiesInGroupModel } from "components/map/contexts/MapContextState";
import { IpdbLegend } from "components/map/legend/IpdbLegend";
import usePlayRadiusDefaults from "components/org-settings/hooks/usePlayRadiusDefaults";
import {
  useScreenshotContext,
  useScreenshotPresetContext
} from "components/screenshot/hooks";
import { ParentDimensionsT } from "components/shared/charts/legend/Legend";
import { useHaveUserSettingsAnswered } from "components/user/hooks";
import { Screenshot } from "components/vis";
import { buildXdaPersistedSettings } from "components/vis/viewerPersistence";

import { PAD_SCENARIO_SERVICE_ADDRESS } from "../../constants/app.constants";
import { lightenColor } from "../../utils/color/lightenColor";
import { getIpdbBin } from "../../utils/getIpdbBin";
import { ScenarioStickListKey } from "../pad-generator/hooks/useScenarioSticks";
import { Stick } from "../pad-generator/models/scenario";
import {
  IpdbCell,
  LIVE_AXIS_GAP,
  LIVE_GRID_BASE,
  centroidPassScene,
  clearMeasurementData,
  clearSpacingMeasurement,
  drawAxis,
  drawCentroid,
  drawDucWells,
  drawIpdb3dModel,
  drawShape,
  drawSpacingMeasurement,
  drawZoneLine,
  getLiveLayoutGrid,
  handleMeasurementTool,
  releaseIpdbGridResources,
  setValueLabelsVisibility
} from "../pad-study/drawHelper";
// PARKED (EVA-163 comment 163139) — the spacing legend is removed from the
// widget and the screenshot. The component file is left in place so this is an
// uncomment rather than a rewrite.
// import XdaSpacingLegend from "./XdaSpacingLegend";
import XdaToolbar from "./XdaToolbar";
import { useXdaHasRestoredSources, useXdaSettingsStore } from "./context";
import {
  IpdbBin,
  IpdbColors,
  IpdbNumOfColorsForPalettes,
  XDA_RADIUS_TYPES
} from "./context/types";
import { selectXdaSettings } from "./context/xdaSettingsSelectors";
import { useMapEntitiesQuery, useXdaConstraint } from "./hooks";
import { useShowXdaInfoRef } from "./hooks/useShowXdaInfoRef";
import checkDataForFieldIsAllZeroes from "./util/checkDataForFieldIsAllZeroes";
import convertHexToRGBA from "./util/convertHexToRgba";
import {
  collectDucWellUwis,
  ducInterceptPositions,
  scaleDucWells
} from "./util/ducWells";
import { getMatchingUwiIndices } from "./util/getMatchingUwiIndices";
import {
  resolveOctantThresholds,
  resolveSpacingMaxRadii,
  resolveVisibleZones // PARKED — the ordered list form was only for the legend.
  // resolveVisibleZoneList
} from "./util/octantZone";
import { shouldClearSpacingSelection } from "./util/spacingKeyboard";
import {
  nextPinnedEntryIndex,
  resolveEntryIndexForUwi,
  resolvePinnedEntryAfterReload,
  resolveSpacingAnchor
} from "./util/spacingSelection";
import { filterInterceptingSticks } from "./util/stickIntersection";
import { isLassoFilterPolygon } from "./utils/isLassoFilterPolygon";

const XdaEndpoint = process.env.REACT_APP_XDA_SERVICE;
const params = {
  fullscreen: false,
  autostart: true
};

const NullColor = "gray";

const emptyScene: XdaData = {
  available_plays: [],
  centroids: [],
  polygons_not_in_list: [],
  lsds: [],
  otherLsds: [],
  ids: [],
  error_message: "",
  polygons: [],
  scale_factor: 0,
  rect: undefined,
  line: undefined,
  intercept_points: [],
  well_data: [],
  duc_wells: []
};

function computeLayoutGrid(settings): XdaLayoutOptions["grid"] {
  const textTops = "AAAAAAAAAAA";
  const textGrid = "0000";
  return {
    top: 50,
    left: settings?.topsLabelsFontSize
      ? Number(
          calculateTextDimensions(textTops, settings.topsLabelsFontSize, "sans-serif")
            .width
        ) + 24
      : 90,
    right: settings?.gridLabelsFontSize
      ? Number(
          2 *
            calculateTextDimensions(textGrid, settings.gridLabelsFontSize, "sans-serif")
              .width
        ) + 93
      : 115,
    bottom: settings?.gridLabelsFontSize
      ? Number(
          calculateTextDimensions(textGrid, settings.gridLabelsFontSize, "sans-serif")
            .height
        ) + 34
      : 50
  };
}

// helper fucntion to check xda out of view (both line and intercept points)
function isXdaOutOfView(
  polygon: Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon | null | undefined,
  line: readonly (readonly number[])[] | null | undefined,
  interceptPoints: { x: number; y: number }[] | null | undefined
): boolean {
  if (!polygon || !line || line.length < 2) return false;
  const turfLine = turf.lineString([...line]);
  const lineOutOfView =
    !turf.booleanWithin(turfLine, polygon) && !turf.booleanCrosses(turfLine, polygon);
  if (lineOutOfView) return true;
  if (interceptPoints?.length > 0) {
    return !interceptPoints.some((pt) =>
      turf.booleanPointInPolygon(turf.point([pt.x, pt.y]), polygon)
    );
  }
  return false;
}

/**
 * Stands in for the map selection on a host that owns its section. One frozen
 * object, module level: a fresh `{}` each render would be a new reference in
 * the request effect's dependencies and would send the section again on every
 * render.
 */
const NO_MAP_SELECTION: Readonly<Record<string, ISelectedWell>> = Object.freeze({});

/**
 * `screenshotContainerId` and `tooltipId` are DOM ids, looked up by
 * `document.getElementById` and by ReactTooltip. Two live instances sharing
 * them makes the second one unreachable, so any host that mounts a second
 * viewer beside a dashboard widget passes its own.
 *
 * `toolbarContainer` is for a host with a toolbar row of its own, such as the
 * well card panel: pass the element and this viewer's toolbar renders there,
 * always visible, instead of appearing over the plot on hover. Pass nothing
 * and a dashboard widget keeps that hover overlay unchanged.
 *
 * `referenceUwi` is for a host that already knows which well the section is
 * about. On a dashboard the spacing measurements are a click: the user clicks a
 * well in the plot and the distances are drawn from it. A host that passes this
 * gets that click for free on every load, so the card opens with its own well
 * as the reference. Pass nothing and nothing is pinned until the user clicks,
 * exactly as today.
 *
 * `screenshotWidgetId` is who this viewer is when its camera is pressed. The
 * default is the viewer's KIND, which is what a dashboard widget has always
 * announced - and is why a second XDA on the page, the well card's, went into
 * screenshot mode alongside it and left the capture with two candidates. A
 * host that mounts one beside a widget passes its own.
 *
 * `sectionFromLineOnly` is for a host that decides its own section, the well
 * card being the one: its section belongs to the card's well, and the well
 * list comes from the line request the card derived. On a dashboard widget the
 * map selection is how the user picks what the section holds, so it is part of
 * the request there and this stays off.
 */
function XdaViewer({
  id,
  widgetType,
  onFullscreenToggle,
  hideFullscreen = false,
  screenshotContainerId = "xda-screenshot-overlay",
  screenshotWidgetId = "xda",
  tooltipId = "xda-tooltip",
  toolbarContainer = null,
  referenceUwi = null,
  sectionFromLineOnly = false
}: {
  id?: string;
  widgetType?: string;
  onFullscreenToggle: (fullscreen: boolean) => void;
  hideFullscreen?: boolean;
  screenshotContainerId?: string;
  screenshotWidgetId?: string;
  tooltipId?: string;
  toolbarContainer?: Maybe<HTMLElement>;
  referenceUwi?: Maybe<string>;
  sectionFromLineOnly?: boolean;
}) {
  // Prefetch play radius defaults on widget open; React Query deduplicates
  // across multiple XDA widget instances via the shared cache.
  usePlayRadiusDefaults();

  // store state and setter
  const xdaSettings = useXdaSettingsStore(useShallow(selectXdaSettings));
  const setXdaSettings = useXdaSettingsStore((s) => s.setXdaSettings);
  const {
    upHeight,
    downHeight,
    leftWidth,
    ipdbTitleSize,
    ipdbFontSize,
    ipdbOpacity,
    showIpdbLegend,
    rightWidth,
    showCompletion,
    scaleByOption,
    scaleByValue,
    completionLength,
    showGrid,
    showMeasurement,
    showXdaValueInfo,
    showTooltip,
    showIpdb,
    topsModelSource,
    dataFields,
    showData,
    ipdbSource,
    hangWellsToTop,
    bin: xdaBin,
    reverseColor,
    showAllTops,
    showOverlap,
    showDucWells,
    widthScaled,
    showRelativeDepth,
    showTVDSSDepth,
    selectedPlays,
    resetZuiInstance,
    selectedRadius,
    spacingClassFilter,
    octantLengthOverrides,
    octantHorCutoffDeg,
    octantVertCutoffDeg
  } = xdaSettings;

  // Live settings for the IMPERATIVE overlay redraws (EVA-163).
  //
  // The hover/click handlers are bound to Two.js shapes when the scene is
  // built, so a handler captures whatever `drawSpacingOverlay` closure existed
  // at that moment. Spacing settings deliberately do NOT rebuild the scene —
  // they redraw the overlay alone — which means the bound handlers would go on
  // calling a closure holding the settings from the LAST rebuild. Symptom:
  // change a control, hover a different well, and the overlay is drawn with
  // the pre-change parameters until something forces a rebuild (redrawing the
  // XDA line, or a reload).
  //
  // Reading through a ref keeps `drawSpacingOverlay` referentially stable, so
  // the handlers never go stale and never need rebinding.
  const xdaSettingsRef = useRef(xdaSettings);
  xdaSettingsRef.current = xdaSettings;

  // Marks Custom mode; passed to the drawing helpers as the _isCustomMode flag.
  const isCustomMode = selectedRadius === XDA_RADIUS_TYPES.Custom;

  /* PARKED (EVA-163 comment 163139) — the zones the Class Filter leaves
     visible, in legend order. Only the legend consumed this; the overlay
     resolves the filter itself, as a Set, at draw time.
  const visibleSpacingZones = useMemo(
    () => resolveVisibleZoneList(spacingClassFilter),
    [spacingClassFilter]
  );
  */

  const sessionKey = xdaSessionKey(id, widgetType);
  // Whether the settings READ has come back — see the session write below.
  const haveUserSettingsAnswered = useHaveUserSettingsAnswered();
  // Whether this viewer was rebuilt from saved state — see the same write.
  const hasRestoredSources = useXdaHasRestoredSources();
  const { lockMap } = useDashboardContext();
  const { convert, getDisplayUnit } = useLengthConversion();
  const { widget, settings } = useScreenshotContext();
  const { selectedPreset } = useScreenshotPresetContext();
  const prevPresetIdRef = useRef<string | undefined>(undefined);
  const dashboardDispatch = useDashboardDispatch();
  const updateIpdbState = useIpdbStore((s) => s.update);
  const setAttentionWells = useMapStore((s) => s.setAttentionWells);
  const setXdaInterceptData = useMapStore((s) => s.setXdaInterceptData);
  const highlightWells = useRef<readonly string[]>([]);
  // state
  const [wellLabel, setWellLabel] = useState("");
  // EVA-185: UWIs of the dot-only wells in the latest /line response, handed to
  // the toolbar so "Filter to wells" includes them while the dots are showing.
  const [ducUwisInScene, setDucUwisInScene] = useState<string[]>([]);
  const [labelTop, setLabelTop] = useState(100);
  const [labelLeft, setLabelLeft] = useState(100);
  const [errorMsg, setErrorMsg] = useState("");
  const [binUpdateErrorMessage, setBinUpdateErrorMessage] = useState("");
  const [parentDimensions, setParentDimensions] = useState<ParentDimensionsT>(undefined);
  const [isLineOutOfView, setIsLineOutOfView] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [availablePlays, setAvailablePlays] = useState<string[]>([]);
  const hasFetchedRef = useRef(false);
  // Tracks the last plays key seen by the warning block so a fresh 1-play
  // response doesn't flash "Select a play" before auto-select fires.
  const prevWarningPlaysKeyRef = useRef("");
  // True from when a new XDA request is queued (line drawn/changed) until its
  // response arrives. While pending, availablePlays still reflects the PREVIOUS
  // line, so play warnings are suppressed to avoid a stale flash (e.g. a brief
  // "No surface sources" before the new region's plays load).
  const [isXdaPending, setIsXdaPending] = useState(false);
  // Always-current ref of what renderScene should receive — updated every render.
  // Used by stale-closure callbacks (e.g. checkLineVisibilityDebounce) so they
  // never accidentally reveal the visualization when no play is selected.
  const renderDataRef = useRef<XdaData>(emptyScene);
  const [screenshotMounted, setScreenshotMounted] = useState(false);
  const attentionWells = useMapStore(selectAttentionWells);
  const geoRectanglesRef = useRef<Maybe<IpdbCell[]>>([]);
  // Single reusable Two.js highlight rect for ipdb cell hover — replaces the old
  // per-cell SVG fill mutation, which no longer applies now that the ipdb grid
  // is one rasterized sprite (EVA-6911). (Re)created by drawIpdb3dModel on every
  // render, in the correct draw-order position (below zone lines/wells/tops);
  // onMouseMove/onMouseOut here only reposition/show/hide it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ipdbHighlightRectRef = useRef<any>(null);
  // state from store
  const selectedWells: { [name: string]: ISelectedWell } =
    useMapStore(selectSelectedWells);
  /*
   * What the map has selected, as far as THIS SECTION is concerned.
   *
   * A bare map click is not a retarget: it opens the well card's preview popup
   * and dual-writes the selection, which the rest of the dashboard follows. But
   * the selection is both part of the /line payload and a dependency of the
   * effect that sends it, so on a host that owns its section that click fetched
   * the section again and added the clicked well to it - a card pinned to one
   * well redrew around another. A host that owns its section reads no selection
   * here; a dashboard widget reads it exactly as before.
   */
  const sectionSelection = sectionFromLineOnly ? NO_MAP_SELECTION : selectedWells;
  const txnIdId = useMapStore(selectTxnIdId);

  // XdaViewer renders inside a react-reverse-portal InPortal which is NOT
  // wrapped by MapProvider, so useContext(MapContext) always returns the
  // static initial state. Fetch entitiesInGroup independently via react-query
  // using the same cache key pattern as useGetXdaProjection.
  //
  // Not gated by useShouldFetchEntitiesOnMap(): that hook's fetch policy is
  // based on the 2D map widget's own zoom/well-count, which is unrelated to
  // this viewer's state and would silently disable its entity coloring
  // whenever the 2D map (if present on the same dashboard) is zoomed out.
  const { data: entitiesOnMap, isFetching: entitiesOnMapFetching } = useQuery<
    Record<string, EntitiesInGroupModel>
  >([ENTITIES_ON_MAP_QUERY_KEY, txnIdId], () => fetchEntitiesOnMap(txnIdId), {
    enabled: !!txnIdId,
    staleTime: ENTITIES_ON_MAP_STALE_TIME,
    cacheTime: MAP_QUERY_CACHE_TIME
  });

  // Flat uwi → color lookup derived from entitiesOnMap. Used in group mode
  // to colour XDA well rectangles with the same colour shown on the map.
  const groupModeUwiColors = useMemo<Record<string, string>>(() => {
    if (!entitiesOnMap) return {};
    const lookup: Record<string, string> = {};
    for (const group of Object.values(entitiesOnMap)) {
      for (const [uwi, color] of Object.entries(group.uwis)) {
        lookup[uwi] = color;
      }
    }
    return lookup;
  }, [entitiesOnMap]);

  // isGroupMode has two independent sources:
  // 1. isMapGroupMode — Redux global group-by is active. In this mode the map
  //    renders synthetic group entities; selectedWells will have no memberUwis
  //    until the user explicitly clicks a group, so isGroupModeSelection alone
  //    would return false even though we are in group mode.
  // 2. isGroupModeSelection — a group entity was clicked and selectedWells
  //    contains entries with memberUwis (single-well mode with an explicit
  //    group selection).
  //
  // Safety: groupMode:true is forwarded to the XDA service alongside `wells`.
  // When isMapGroupMode is true but no XDA line has been drawn yet, `wells`
  // will be empty and the mutation is effectively a no-op — the XDA service
  // returns an empty result for an empty well list regardless of groupMode.
  const isMapGroupMode = useSelector((state: RootState) =>
    Boolean(state.groupBy.globalGroupBy?.mode)
  );
  const isGroupMode = isMapGroupMode || isGroupModeSelection(sectionSelection);
  const currentPolygon = useSelector((state: RootState) => state.filter.polygonFilter) as
    | Polygon
    | MultiPolygon
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | Feature<Polygon | MultiPolygon, { [name: string]: any }>;

  const xdaLineRequest = useMapStore(selectXdaLineRequest);
  const isXdaLineReversed = useMapStore(selectIsXdaLineReversed);
  const showXdaValueInfoRef = useShowXdaInfoRef();
  const checkedPadScenarios = useSelector(
    (state: RootState) => state.app.checkedPadScenarios
  );
  const noWellsAndSticks =
    Object.keys(selectedWells).length === 0 &&
    checkedPadScenarios.length === 0 &&
    xdaLineRequest.wells.length === 0;
  // Plays exist for this location but the user has not chosen one yet — the
  // only case where the whole scene is deliberately held back (the user is
  // asked to pick a play first). When no play exists at all the scene still
  // draws, so the cross-section frame is visible instead of a blank canvas.
  const awaitingPlaySelection =
    noWellsAndSticks && availablePlays.length > 0 && selectedPlays.length === 0;
  // Derive sticks from React Query cache (same keys used by ScenarioList) so
  // we avoid storing large stick arrays in Redux.
  const xdaStickApiUrl = `${PAD_SCENARIO_SERVICE_ADDRESS}/api/v1/pad-scenario`;
  const checkedSticksQueries = useQueries(
    checkedPadScenarios.map((scenario) => ({
      queryKey: [ScenarioStickListKey, { $oid: scenario.scenarioId }],
      queryFn: (): Promise<Stick[]> =>
        axios
          .get<Stick[]>(`${xdaStickApiUrl}/${scenario.scenarioId}/sticks`)
          .then((r) => r.data),
      refetchOnWindowFocus: false,
      enabled: !!scenario.scenarioId
    }))
  );
  // Memoized to produce a stable reference — without this, .map()+spread creates a
  // new array every render, causing the requestDebounce useEffect to fire on every
  // render and triggering an infinite mutate() loop.
  const checkedPadScenariosWithSticks = useMemo(
    () =>
      checkedPadScenarios.map((scenario, i) => ({
        ...scenario,
        sticks: checkedSticksQueries[i]?.data ?? []
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkedPadScenarios, ...checkedSticksQueries.map((q) => q.data)]
  );
  const { defaultSignificantFigures, useStrictRounding } = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );
  // refs
  const labelRef = useRef(null);
  const sceneRef = useRef(null);
  const sceneScreenshotRef = useRef(null);
  const shapeRef = useRef([]);
  const screenSize = useRef<ScreenSize>(null);
  const screenshotScreenSizeRef = useRef<ScreenSize>(null);
  // Lazy init: `new Two(params)` (autostart: true) immediately joins two.js's
  // session-global RAF loop and is pinned forever in Two.Instances (never
  // removed by the library) — evaluating it as a useRef() argument would
  // create and leak a brand-new, permanently RAF-ticking Two instance on
  // EVERY re-render (useRef only keeps the first result; the constructor's
  // side effects from every other call still happen and are never released).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const two = useRef<any>(null);
  if (!two.current) {
    two.current = new Two(params);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const twoScreenshot = useRef<any>(null);
  const zuiRef = useRef(null);
  const zuiScreenshotRef = useRef(null);
  const dataRef = useRef<XdaData>(null);
  // Intentional render-time write: keeps stale-closure callbacks (e.g.
  // checkLineVisibilityDebounce) in sync with the current scene without needing an effect.
  renderDataRef.current =
    dataRef.current && !isXdaPending && !awaitingPlaySelection
      ? dataRef.current
      : emptyScene;
  const pathsRef = useRef([]);
  const pathsScreenshotRef = useRef([]);
  const mouseoverRef = useRef(null);
  const measurementDataRef = useRef({
    clickEvent: null,
    distanceText: null,
    dots: [],
    dotCounter: 0,
    line: null,
    textBackground: null
  });
  // Selected-well spacing measurement (EVA-29). The pin anchors a cross-section
  // ENTRY index (multi-intercept wells repeat a UWI; each entry is independent)
  // and is only meaningful for the XdaData it was created against — it is
  // released whenever scene data reloads. Ref-only, like the hover state below:
  // nothing in this component's output depends on it (the overlay is drawn
  // imperatively and the legend toggles itself), so pinning needs no re-render.
  const selectedEntryIndexRef = useRef<Nullable<number>>(null);
  // Read inside the /line success callback, which is created once and closes
  // over its first render — the same reason xdaSettingsRef exists.
  const referenceUwiRef = useRef<Maybe<string>>(referenceUwi);
  useEffect(() => {
    referenceUwiRef.current = referenceUwi;
  }, [referenceUwi]);
  // Hovering previews the same display; clicking pins it. A pinned selection
  // wins over hover; hover only drives the overlay while nothing is pinned.
  //
  // Hover is deliberately ref-only — NO React state. Setting state here would
  // re-render this whole component at pointer frequency, which re-runs the
  // ipdb legend's dimension/position effects and made its scrollbar flicker
  // on every hover (and violates the pointer-frequency state rule). Nothing in
  // this component's output depends on the hovered entry: the overlay is drawn
  // imperatively, and the legend follows the pinned state instead.
  const hoveredEntryIndexRef = useRef<Nullable<number>>(null);
  // The legend is MOUNTED only while a well is pinned — never for a hover
  // preview, and removed entirely once nothing is selected. This is the one
  // piece of the feature that needs a render, and pinning is a click (not
  // pointer frequency), so a re-render here is free. Keeping the legend out of
  // the DOM when unused also guarantees it cannot affect viewer layout.
  const [isSpacingPinned, setIsSpacingPinned] = useState(false);
  // SEPARATE from the pin flag, and it has to stay that way. `isSpacingPinned`
  // has a second consumer — the Escape-to-unpin listener — so folding "did the
  // draw produce anything" into it strands the user: click a well on a
  // single-well section (or under a filter that admits nobody) and the pin ref
  // is set while the flag is false, so nothing is drawn to show the pin,
  // Escape never attaches, and hover stays dead because `onShapeEntered` only
  // previews while nothing is pinned. The only way out was re-clicking that
  // exact well.
  /* PARKED — gated the legend on the pin having actually produced
     measurements, kept separate from isSpacingPinned because that flag also
     drives the Escape-to-unpin listener.
  const [spacingLegendHasContent, setSpacingLegendHasContent] = useState(false);
  */
  // Whether the last LIVE overlay draw actually produced measurements. The
  // overlay draws nothing when no neighbour survives to be labelled — a
  // single-well cross-section, or filters that admit no one — and a legend of
  // zone colours beside a plot with no zone-coloured anything reads as broken.
  // Written by the draw, read here; the handlers redraw before they sync.
  const spacingHasLabelsRef = useRef(false);
  // Named for the selection, not the legend: it drives the pin flag (which the
  // Escape-to-unpin listener reads) and, when the legend is un-parked, the
  // legend's visibility too. Calling it syncSpacingLegendVisibility described
  // only the half that is currently parked.
  const syncSpacingSelectionState = useCallback(() => {
    const pinned = selectedEntryIndexRef.current !== null;
    setIsSpacingPinned(pinned);
    // PARKED: setSpacingLegendHasContent(pinned && spacingHasLabelsRef.current);
  }, []);
  const spacingDataRef = useRef({ objects: [] });
  const spacingScreenshotDataRef = useRef({ objects: [] });
  // Old Well Data value labels, collected per context so the spacing display
  // can hide them while it is showing (they crowd the same space around the
  // dots) and restore them when it clears.
  const valueLabelsRef = useRef({ objects: [] });
  const valueLabelsScreenshotRef = useRef({ objects: [] });
  // Cached result of the IPDB color/stops computation
  const ipdbColorCacheRef = useRef<{
    colors: string[];
    stops: [number, string][];
    numOfColors: number;
    hasMinValue: boolean;
    hasMaxValue: boolean;
  } | null>(null);
  // When non-null, renderScene draws into this target instead of the normal canvas
  const renderContextOverride = useRef<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
    size: ScreenSize;
    isScreenshot: boolean;
  } | null>(null);
  // custom hooks
  // Called for its side-effect: syncs fetched play/radius defaults into the XDA
  // dimension settings (upHeight/downHeight/leftWidth/rightWidth). Its returned
  // data is deliberately not consumed here — see the request effect below.
  useXdaConstraint();
  const { mapEntities, mapEntitiesFetching } = useMapEntitiesQuery();
  // True while either well-color source is refetching (e.g. after a filter change
  // bumps txnId). During this window both queries' data is briefly undefined; the
  // render effects below skip repainting so wells keep their last good colors
  // instead of flashing the empty/default coloring until the new colors arrive.
  // OR semantics are intentional: both sources must be settled (isFetching=false)
  // before a repaint is allowed. React Query batches same-cycle state updates, so
  // both sources typically settle in the same render; in the rare staggered case
  // the guard stays active until the slower source catches up.
  const colorsFetching = mapEntitiesFetching || entitiesOnMapFetching;
  // Ref mirrors for long-lived callbacks. react-query v3 snapshots mutation
  // options at mutate() time (MutationObserver.mutate spreads the observer
  // options into the Mutation), so the onSuccess closure below sees
  // request-time values, not response-time ones; the debounced resize and
  // line-visibility callbacks similarly outlive the render that created them.
  // Reading through these refs yields the value at fire time instead.
  // renderSceneRef is written right after renderScene's useCallback below.
  const colorsFetchingRef = useRef(colorsFetching);
  colorsFetchingRef.current = colorsFetching;
  const renderSceneRef = useRef<(data: XdaData) => Promise<void>>();
  const [valueInfo, setValueInfo] = useState("");

  function renderToScreenshotCanvas(data: XdaData): Promise<void> {
    if (!twoScreenshot.current || !screenshotScreenSizeRef.current)
      return Promise.resolve();
    renderContextOverride.current = {
      context: twoScreenshot.current,
      size: screenshotScreenSizeRef.current,
      isScreenshot: true
    };
    return renderScene(data).finally(() => {
      renderContextOverride.current = null;
    });
  }

  const screenshotBounds = {
    width: settings?.width || 1152,
    height: settings?.height || 681
  };

  const screenshotOverlayVisible = useMemo(() => {
    return widget?.widgetId === screenshotWidgetId;
  }, [widget, screenshotWidgetId]);

  const activeIpdbLegend =
    screenshotOverlayVisible && settings?.legendVisible !== undefined
      ? settings.legendVisible
      : showIpdbLegend;

  const activeIpdbTitleSize =
    screenshotOverlayVisible && settings?.legendTitleSize
      ? settings.legendTitleSize
      : ipdbTitleSize;

  // "Legend Label Size" — shared by the 3D geo model legend and the spacing
  // legend, so the two never drift apart in size (EVA-163).
  const activeIpdbFontSize =
    screenshotOverlayVisible && settings?.legendFontSize
      ? settings.legendFontSize
      : ipdbFontSize;

  const activeBackgroundOpacity =
    screenshotOverlayVisible && settings?.legendOpacity
      ? settings.legendOpacity
      : ipdbOpacity;

  const activeShowLegendBorder =
    screenshotOverlayVisible && settings?.legendBorderVisible !== undefined
      ? settings.legendBorderVisible
      : true;

  /* PARKED (EVA-163 comment 163139) — the legend's screenshot settings.
     Its whole controls group is commented out in
     useXDAScreenshotSettingsControls, so nothing writes these any more.
  // EVA-29 spacing legend — its own screenshot settings group ("Spacing
  // Legend"), independent of the 3D geo model legend's.
  const activeSpacingLegendVisible =
    screenshotOverlayVisible && settings?.spacingLegendVisible !== undefined
      ? Boolean(settings.spacingLegendVisible)
      : true;

  const activeSpacingLegendOpacity =
    screenshotOverlayVisible && settings?.spacingLegendOpacity !== undefined
      ? Number(settings.spacingLegendOpacity)
      : 1;

  const activeSpacingLegendBorder =
    screenshotOverlayVisible && settings?.spacingLegendBorderVisible !== undefined
      ? Boolean(settings.spacingLegendBorderVisible)
      : true;
  */

  // Primary (dashboard) canvas. Screenshot-only label sizes must not re-lay-out the
  // visible widget — only the dedicated screenshot canvas below reacts to them (see
  // screenshotLayoutOptions). Its margins do follow the live label size, which
  // renderScene resolves per draw; this is the base they never go below.
  const xdaLayoutOptions: XdaLayoutOptions = {
    grid: { ...LIVE_GRID_BASE },
    axisGap: LIVE_AXIS_GAP,
    scale: 1
  };
  // Screenshot canvas always uses screenshot-mode layout (no conditional — settings always apply).
  const screenshotLayoutOptions: XdaLayoutOptions = {
    grid: computeLayoutGrid(settings),
    axisGap: 20,
    scale: 1
  };
  // The grid the live scene was last drawn with. The spacing overlay redraws
  // imperatively on hover, outside renderScene, and has to place its chips against the
  // same grid: the margins move with the label size, and two different grids put the
  // same centroid at different pixels.
  const liveLayoutGridRef = useRef(xdaLayoutOptions.grid);
  // Live ref so renderScene can access without adding it to deps
  const screenshotLayoutOptionsRef = useRef(screenshotLayoutOptions);
  screenshotLayoutOptionsRef.current = screenshotLayoutOptions;
  // Ref so renderScene can read screenshotOverlayVisible without being in its dep array,
  // preventing a cascade of re-renders on every screenshot toggle / settings change.
  const screenshotOverlayVisibleRef = useRef(screenshotOverlayVisible);
  screenshotOverlayVisibleRef.current = screenshotOverlayVisible;

  useEffect(() => {
    if (binUpdateErrorMessage) {
      updateIpdbState({
        legendTitle: "",
        legendItems: []
      });
    }
  }, [binUpdateErrorMessage]);

  const {
    isError,
    isLoading,
    mutate,
    reset: resetMutation
  } = useMutation(
    ({ wells, line, dataFields, groupMode }: MutationParameters) => {
      if (!line || !topsModelSource.length) {
        // Return a resolved promise with null to trigger onSuccess but skip the API call
        return Promise.resolve(null);
      }

      // Filter each scenario's sticks to only include those that intercept the line
      const filteredPadScenarios = checkedPadScenariosWithSticks.map((scenario) =>
        filterInterceptingSticks(scenario, line.start, line.end)
      );

      // Every radius mode (Custom, EVA Research presets, and org-defined
      // presets) stores dimensions in meters — the backend's base unit — so
      // they are sent through unchanged. Custom previously re-applied
      // convertBack here, which double-converted in imperial once EVA-6715
      // switched Custom inputs to store meters.
      return axios.post(`${XdaEndpoint}/line`, {
        line,
        wells,
        upHeight,
        downHeight,
        leftWidth,
        rightWidth,
        showCompletion,
        scaleByOption,
        scaleByValue,
        completionLength,
        showIpdb,
        topsModelSource,
        showAllTops,
        showOverlap,
        showDucWells,
        // load additional user selected fields plus default data fields
        data_fields: dataFields
          .map((field) => field.property)
          .concat(["Header.LicenseNumber", "Header.Status", "Header.GroupName"]),
        data_source: ipdbSource,
        hangWellsToTop: hangWellsToTop,
        geoModelField: xdaSettings.ipdbField,
        padScenarios: filteredPadScenarios,
        selectedPlays,
        defaultSignificantFigures,
        useStrictRounding,
        groupMode
      });
    },
    {
      onSuccess: (response: AxiosResponse<XdaData>) => {
        hasFetchedRef.current = true;
        setIsXdaPending(false);
        // EVA-29: a pin survives a reload when the same well is still in the
        // response — re-resolved by UWI + intercept ordinal, because entry
        // indexes only mean anything within one response's ordering. Toggling
        // "Hang to Top Zone" refetches while returning the same wells, so the
        // selection should not drop out from under the user. dataRef still
        // holds the PREVIOUS response here (it is replaced further down).
        selectedEntryIndexRef.current = resolvePinnedEntryAfterReload(
          dataRef.current?.ids,
          response?.data?.ids,
          selectedEntryIndexRef.current
        );
        // A host's own reference well is re-asserted here, on every response,
        // so a retarget lands on the new well rather than keeping whatever the
        // re-resolve above rescued. It does not fight the user: an in-plot
        // click still wins until the next load. Silent when the well is not in
        // the section — there is nothing to measure from.
        const hostEntryIndex = resolveEntryIndexForUwi(
          response?.data?.ids,
          referenceUwiRef.current
        );
        if (hostEntryIndex !== null) {
          selectedEntryIndexRef.current = hostEntryIndex;
        }
        hoveredEntryIndexRef.current = null;
        syncSpacingSelectionState();
        if (!response?.data) {
          setAvailablePlays([]);
          setDucUwisInScene([]);
          renderScene(emptyScene);
          if (screenshotOverlayVisible) renderToScreenshotCanvas(emptyScene);
          return;
        }

        const data = response.data;

        if (data.error_message) {
          throw data.error_message;
        }
        // Normalize line and intercept points before storing
        const positionalData: Position[] = [
          [data.line.start.x, data.line.start.y],
          [data.line.end.x, data.line.end.y]
        ];
        const normalizedPoints: Position[] = data.intercept_points.map(
          (pt: { x: number; y: number }) => [pt.x, pt.y]
        );
        // EVA-185: dot-only wells get the same orange intercept marker on the
        // map, whether or not the filter holds them. The service returns them
        // only when the toggle is on, so an off toggle clears them on refetch.
        setXdaInterceptData({
          line: positionalData,
          points: normalizedPoints.concat(ducInterceptPositions(data.duc_wells))
        });
        dataRef.current = data;
        setAvailablePlays(data.available_plays ?? []);
        // Hide the entire visualization when no wells/sticks are present, this
        // location has plays, and none has been selected yet. The play count is
        // read off the response rather than the availablePlays state, which is
        // still the previous line's value inside this frozen closure.
        const renderData =
          noWellsAndSticks &&
          (data.available_plays?.length ?? 0) > 0 &&
          selectedPlays.length === 0
            ? emptyScene
            : data;
        // EVA-185: offer the toolbar only the dots that are actually on screen.
        // Set from `renderData`, not `data`: while the play picker is
        // suppressing the section nothing is drawn, so "Filter to wells" must
        // not count or apply dot-wells the user cannot see.
        setDucUwisInScene(
          renderData === emptyScene ? [] : collectDucWellUwis(data.duc_wells)
        );
        // Skip painting if color queries are still mid-refetch (e.g. the same
        // filter/txnId change that triggered this /line request also started a
        // color refetch). The visual-changes / groupColors effects below will
        // repaint once the colors settle, at which point renderDataRef.current
        // already holds this response's data.
        // Both reads go through refs: react-query v3 freezes this closure at
        // mutate() time, so the plain `colorsFetching`/`renderScene` bindings
        // here hold request-time values — including renderScene's color maps.
        if (!colorsFetchingRef.current) {
          setIsRendering(true);
          renderSceneRef
            .current(renderData)
            .then(() => {
              if (screenshotOverlayVisibleRef.current)
                renderToScreenshotCanvas(renderData);
            })
            .finally(() => {
              requestAnimationFrame(() => {
                setIsRendering(false);
              });
            });
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (err: any) => {
        hasFetchedRef.current = true;
        setIsXdaPending(false);
        selectedEntryIndexRef.current = null;
        hoveredEntryIndexRef.current = null;
        syncSpacingSelectionState();
        if (err.response?.data) {
          setErrorMsg(err.response.data);
        } else if (typeof err === "string") {
          setErrorMsg(err);
        } else {
          setErrorMsg("network error occurred");
        }
        setAvailablePlays([]);
        // EVA-185: drop the dot-wells with the rest of the response-derived
        // state. Leaving them would keep "Filter to wells" enabled off a
        // section that is no longer on screen, and applying it would filter to
        // wells from the failed request.
        setDucUwisInScene([]);
        renderScene(emptyScene);
        if (screenshotOverlayVisible) renderToScreenshotCanvas(emptyScene);
      }
    }
  );

  // EVA-29: draw (or clear) the spacing overlay for one rendering context.
  // The anchor is the pinned selection, falling back to the hovered entry.
  // While the overlay shows, the old Well Data value labels are hidden (they
  // crowd the same space around the dots); they come back when it clears.
  // `scaledData` must be the scaled scene data the context was drawn from and
  // `layout.scale` must carry the same x-scale factor.
  const drawSpacingOverlay = useCallback(
    (
      context,
      registryRef,
      labelsRegistryRef,
      scaledData: XdaData,
      layout,
      size: ScreenSize
    ) => {
      const anchor = resolveSpacingAnchor(
        selectedEntryIndexRef.current,
        hoveredEntryIndexRef.current
      );
      if (anchor == null || !scaledData?.centroids?.length || !size) {
        clearSpacingMeasurement(registryRef, context);
        setValueLabelsVisibility(labelsRegistryRef, context, true);
        return false;
      }
      const displayUnit = getDisplayUnit();
      // Through the ref, never the closed-over value — see xdaSettingsRef.
      const settingsNow = xdaSettingsRef.current;
      const drewLabels = drawSpacingMeasurement(
        context,
        scaledData,
        layout,
        size,
        settingsNow,
        anchor,
        registryRef,
        displayUnit,
        // EVA-163: the widget's own octant parameters layered over the
        // unit-native defaults. Length overrides are looked up under the SAME
        // display unit they were typed in, so nothing is ever converted.
        resolveOctantThresholds(displayUnit, {
          lengths: settingsNow.octantLengthOverrides,
          horCutoffDeg: settingsNow.octantHorCutoffDeg,
          vertCutoffDeg: settingsNow.octantVertCutoffDeg
        }),
        resolveVisibleZones(settingsNow.spacingClassFilter),
        resolveSpacingMaxRadii(displayUnit, settingsNow.octantLengthOverrides)
      );
      // Only hide the old Well Data value labels if the overlay actually took
      // the space — with nothing drawn they would be hidden for no reason.
      setValueLabelsVisibility(labelsRegistryRef, context, !drewLabels);
      return drewLabels;
    },
    [getDisplayUnit]
  );

  // EVA-29: imperative redraw of the live overlay for hover/click interactions
  // — a hover or selection change must never rebuild the scene (EVA-6911).
  //
  // Deliberately does NOT call scaleData: that deep-copies every polygon,
  // intercept point and lsd on a pointer-frequency path, while the overlay
  // reads only rect and centroids. Scaling those two here keeps the work
  // proportional to what is actually consumed.
  const redrawSpacingOverlayNow = useCallback(() => {
    const sceneData = dataRef.current;
    const anchor = resolveSpacingAnchor(
      selectedEntryIndexRef.current,
      hoveredEntryIndexRef.current
    );
    // With nothing anchored the overlay only clears, so skip scaling entirely
    // — mouseleave used to pay for a full scene copy it never read.
    const overlayData =
      anchor == null || !sceneData?.rect
        ? sceneData
        : ({
            rect: sceneData.rect,
            centroids: (sceneData.centroids ?? []).map((c) => ({
              x: c.x * widthScaled,
              y: c.y
            }))
          } as XdaData);
    spacingHasLabelsRef.current = drawSpacingOverlay(
      two.current,
      spacingDataRef,
      valueLabelsRef,
      overlayData,
      { ...xdaLayoutOptions, grid: liveLayoutGridRef.current, scale: widthScaled },
      screenSize.current
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthScaled, drawSpacingOverlay]);

  const onShapeEntered = useCallback(
    (evt, path, id, selectedWells, wellData: string[]) => {
      if (screenshotOverlayVisibleRef.current) {
        return;
      }
      // EVA-29: hovering previews the spacing measurement anchored to this
      // entry; clicking pins it (onShapeClicked). A pinned selection wins.
      if (path.entryIndex != null) {
        hoveredEntryIndexRef.current = path.entryIndex;
        if (selectedEntryIndexRef.current == null) {
          redrawSpacingOverlayNow();
          syncSpacingSelectionState();
        }
      }
      if (id.unique_id in selectedWells) {
        const well = selectedWells[id.unique_id];
        // Highlight all paths matching this UWI (multi-intercept U-shape wells)
        const enterIndices = getMatchingUwiIndices(
          pathsRef.current.map((p) => p.uwi),
          id.unique_id
        );
        for (const i of enterIndices) {
          pathsRef.current[i].fill = well.color;
        }
        if (!labelRef.current) {
          return;
        }
        setLabelLeft(evt.offsetX);
        setLabelTop(evt.offsetY);
        setAttentionWells([id.unique_id]);
        const uwi = new Uwi().toFormatted(id.unique_id);
        let label = uwi + "<br/>" + well.group;
        if (wellData?.length >= 4 && dataFields.length >= 4) {
          for (let i = 0; i < 4; i++) {
            const value = wellData[i];
            if (value.length > 0 && dataFields[i].title) {
              label += "<br/>" + dataFields[i].title + ": " + value;
            }
          }
        }
        setWellLabel(label);
        if (showTooltip) {
          ReactTooltip.show(labelRef.current);
        }
      }
    },
    [
      setAttentionWells,
      showTooltip,
      dataFields,
      redrawSpacingOverlayNow,
      syncSpacingSelectionState
    ]
  );

  const onShapeLeave = useCallback(
    (_, path) => {
      if (screenshotOverlayVisibleRef.current) {
        return;
      }
      // EVA-29: leaving the well clears the hover preview; a pinned selection
      // stays (the redraw resolves to the pinned anchor, or clears when none).
      hoveredEntryIndexRef.current = null;
      if (selectedEntryIndexRef.current == null) {
        redrawSpacingOverlayNow();
      }
      // Unhighlight all paths matching this UWI (multi-intercept U-shape wells).
      // In overlap mode, revert to semi-transparent fill (0.3) rather than fully
      // transparent — shapes intentionally stay visible to show the overlap area.
      const leaveIndices = getMatchingUwiIndices(
        pathsRef.current.map((p) => p.uwi),
        path.uwi
      );
      for (const i of leaveIndices) {
        const p = pathsRef.current[i];
        p.fill = showOverlap
          ? convertHexToRGBA(p.stroke, 0.3)
          : p.defaultFill ?? "rgba(0,0,0,0)";
      }
      ReactTooltip.hide(labelRef.current);
      setAttentionWells([]);
    },
    [setAttentionWells, showOverlap, redrawSpacingOverlayNow, syncSpacingSelectionState]
  );

  // EVA-29: Escape releases the pinned well (and with it the legend, which is
  // mounted only while pinned). Listener is attached only while something is
  // pinned, so the viewer never holds a global key handler it does not need.
  useEffect(() => {
    if (!isSpacingPinned) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !shouldClearSpacingSelection(
          event.key,
          event.target,
          screenshotOverlayVisibleRef.current
        )
      ) {
        return;
      }
      selectedEntryIndexRef.current = null;
      hoveredEntryIndexRef.current = null;
      redrawSpacingOverlayNow();
      syncSpacingSelectionState();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSpacingPinned, redrawSpacingOverlayNow, syncSpacingSelectionState]);

  // EVA-29: pin/unpin the spacing measurement. Hover already previews the
  // display; clicking a well makes it stay (and reveals the legend), clicking
  // the same well again releases it (the hover preview takes back over until
  // the mouse leaves). Empty space never changes the pin (drawShape only fires
  // this from a well path, and suppresses it while the ruler is enabled).
  const onShapeClicked = useCallback(
    (entryIndex: number) => {
      if (screenshotOverlayVisibleRef.current) return;
      const next = nextPinnedEntryIndex(selectedEntryIndexRef.current, entryIndex);
      selectedEntryIndexRef.current = next;
      redrawSpacingOverlayNow();
      syncSpacingSelectionState();
    },
    [redrawSpacingOverlayNow, syncSpacingSelectionState]
  );

  const scaleData = (data: XdaData, scale: number): XdaData => {
    const scaledData = Object.assign({}, data);
    if (!data.rect) {
      return scaledData;
    }
    scaledData.rect = {
      width: data.rect.width,
      height: data.rect.height,
      miny: data.rect.miny,
      maxy: data.rect.maxy,
      widthScaled: data.rect.width * scale
    };
    scaledData.centroids = data.centroids.map((centroid) => {
      return { x: centroid.x * scale, y: centroid.y };
    });
    scaledData.intercept_points = data.intercept_points.map((point) => {
      return { x: point.x * scale, y: point.y };
    });
    scaledData.duc_wells = scaleDucWells(data.duc_wells, scale);
    scaledData.polygons = data.polygons.map((polygon) => {
      return polygon.map((point) => {
        return { x: point.x * scale, y: point.y };
      });
    });
    scaledData.polygons_not_in_list = data.polygons_not_in_list.map((polygon) => {
      return {
        unique_id: polygon.unique_id,
        points: polygon.points.map((point) => {
          return { x: point.x * scale, y: point.y };
        }),
        centroid: { x: polygon.centroid.x * scale, y: polygon.centroid.y }
      };
    });
    scaledData.lsds = data.lsds.map((lsd) => {
      return {
        lsd: lsd.lsd,
        zones: lsd.zones.map((zone) => {
          return {
            zone: zone.zone,
            line: {
              start: { x: zone.line.start.x * scale, y: zone.line.start.y },
              end: { x: zone.line.end.x * scale, y: zone.line.end.y }
            }
          };
        })
      };
    });

    scaledData.ipdb3d_boxes = data.ipdb3d_boxes?.map((box) => {
      return {
        bot: box.bot,
        top: box.top,
        left: { x: box.left.x * scale, y: box.left.y },
        right: { x: box.right.x * scale, y: box.right.y },
        val: box.val
      };
    });

    return scaledData;
  };

  const renderScene = useCallback(
    async (data: XdaData) => {
      // Resolve render target — override when rendering to the screenshot canvas
      const activeOverride = renderContextOverride.current;
      const context = activeOverride?.context ?? two.current;
      const activeScreenSize = activeOverride?.size ?? screenSize.current;
      const isScreenshotContext =
        activeOverride?.isScreenshot ?? screenshotOverlayVisibleRef.current;
      if (!activeOverride) {
        liveLayoutGridRef.current = getLiveLayoutGrid(
          activeScreenSize,
          data,
          xdaSettings
        );
      }
      const activeLayout = activeOverride
        ? screenshotLayoutOptionsRef.current
        : { ...xdaLayoutOptions, grid: liveLayoutGridRef.current };
      const activePaths = activeOverride ? pathsScreenshotRef : pathsRef;
      const selectedWellsAndSticks = { ...selectedWells };

      if (!data) {
        return;
      }

      const ipdbBin = getIpdbBin(
        xdaBin?.lessThan,
        xdaBin?.binSize,
        xdaBin?.greaterThan,
        false
      );

      // Always normalise binSize to a number — user input may arrive as a string,
      // and the screenshot canvas skips the async block where this was previously done.
      if (xdaBin?.binSize) {
        ipdbBin.binSize =
          typeof xdaBin.binSize === "string"
            ? parseFloat(xdaBin.binSize)
            : xdaBin.binSize;
      }

      // Skip async bin computation for the screenshot canvas — the primary render
      // already computed and stored the correct bin in xdaBin state.
      if (!activeOverride && showIpdb && data.ipdb3d_boxes?.length > 0) {
        let defaultBinValues = null;
        const flatArray = data.ipdb3d_boxes.map((box) => box.val);
        const p10p90 = getP10P90Percentile(flatArray);
        if (p10p90?.length) {
          defaultBinValues = await getDynamicBinSettings({
            p10: p10p90[0],
            p90: p10p90[1]
          });
        }

        if (defaultBinValues) {
          // TECHNICAL DEBT: PREMIUM EDITION
          // SORRY FUTURE ME
          // HACK: We will hardcode bin size to 1 and max bin to 8 for McDaniel Montney porosity
          // as per request from Prod team
          const shouldHardcode =
            xdaSettings.ipdbSource == "McDaniel" &&
            xdaSettings.ipdbField === "Porosity" &&
            data.lsds?.every((lsd) =>
              lsd.zones?.every((zone) => zone.zone?.startsWith("MONT"))
            );

          const defaultIpdbBin = shouldHardcode
            ? { ...defaultBinValues, binSize: 1, greaterThan: 8 }
            : defaultBinValues;

          if (
            (!xdaBin?.isLocked || !xdaBin?.binSize) &&
            (ipdbBin.lessThan !== defaultIpdbBin.lessThan ||
              ipdbBin.binSize !== defaultIpdbBin.binSize ||
              ipdbBin.greaterThan !== defaultIpdbBin.greaterThan)
          ) {
            // since we are updating the bin, we should return and not render the scene
            // this will allow the useEffect to re-render the scene with the new bin
            defaultIpdbBin.isLocked = false;
            setXdaSettings({
              ...xdaSettings,
              bin: defaultIpdbBin
            });
            return;
          }
        } else {
          setBinUpdateErrorMessage("Selected field has no data.");
          setXdaSettings({ ...xdaSettings, bin: null });
          return;
        }
      }

      const isLassoFilter = isLassoFilterPolygon(currentPolygon);
      if (
        !activeOverride &&
        currentPolygon &&
        !isLassoFilter &&
        xdaLineRequest.line?.length >= 2
      ) {
        if (
          isXdaOutOfView(
            currentPolygon,
            xdaLineRequest.line,
            dataRef.current?.intercept_points
          )
        ) {
          setIsLineOutOfView(true);
          return;
        }
      }

      // Clear current scene
      context.clear();
      if (!activeOverride) {
        removeShapeEventBinding();
        clearMeasurementData(measurementDataRef, context, true);
        setIsLineOutOfView(false);
      }

      if (activeOverride && currentPolygon && xdaLineRequest.line?.length >= 2) {
        const line = turf.lineString(xdaLineRequest.line);
        if (
          !turf.booleanWithin(line, currentPolygon) &&
          !turf.booleanCrosses(line, currentPolygon)
        ) {
          return;
        }
      }

      if (
        !data?.available_plays?.length &&
        mapEntities?.status === 200 &&
        !mapEntities.data.length &&
        !data?.polygons?.some((p) => p.length > 0) &&
        // EVA-185: a section holding nothing but dot-wells is still a section.
        !data?.duc_wells?.length
      ) {
        // this is the case where we have crossed wells but no data is returned
        // If map request is successful but no data is returned, stop drawing the new scene
        // also need to check if this is a no cross section scenario, where we will need to drawing the new scene no matter what
        // Skip this guard in group mode where polygons are present but mapEntities is empty
        // (selectedWells is always empty in group mode so mapEntities is never populated).
        return;
      }
      data = scaleData(data, widthScaled);
      activeLayout.scale = widthScaled;
      let selectedEntities: { [uwi: string]: ISelectedWell } = Object.assign(
        {},
        selectedWellsAndSticks
      );

      // Group mode: selectedWells is always empty (no pre-selected entities),
      // so selectedEntities would be empty and drawShape would never be called.
      // Populate it from data.ids so the rectangles are drawn and coloured.
      if (
        isGroupMode &&
        Object.keys(selectedEntities).length === 0 &&
        data?.ids?.length
      ) {
        for (const id of data.ids) {
          selectedEntities[id.unique_id] = {
            // Use the actual group color from the map if available; fall back
            // to a neutral default so the well is at least visible.
            color: groupModeUwiColors[id.unique_id] ?? "#1f77b4",
            Uwi: id.unique_id,
            group: ""
          };
        }
      }

      if (mapEntities?.status === 200) {
        const temp: { [uwi: string]: ISelectedWell } = {};
        const entitiesDict: { [name: string]: MapEntity } = {};
        for (const ent of mapEntities.data) {
          entitiesDict[ent.uwi] = ent;
        }

        for (const selected of Object.keys(selectedEntities)) {
          if (entitiesDict[selected]) {
            temp[selected] = {
              color: entitiesDict[selected].color,
              Uwi: selectedEntities[selected].Uwi,
              group: entitiesDict[selected].group
            };
          }
        }

        // Group mode: selectedEntities is keyed by group composite IDs which
        // won't match entitiesDict (keyed by real UWIs). For any group entry
        // that wasn't matched above, map each member UWI to an entry using the
        // color returned by mapEntities for that UWI. Using entitiesDict color
        // (rather than the stale groupWell.color from the store) ensures colors
        // update whenever txnId changes, matching normal-mode behaviour.
        for (const [groupId, groupWell] of Object.entries(selectedWellsAndSticks)) {
          if (!temp[groupId] && groupWell.memberUwis && groupWell.memberUwis.length > 0) {
            for (const memberUwi of groupWell.memberUwis) {
              if (entitiesDict[memberUwi]) {
                temp[memberUwi] = {
                  color: entitiesDict[memberUwi].color,
                  Uwi: memberUwi,
                  group: groupWell.group
                };
              }
            }
          }
        }

        // Line-intersect wells: UWIs carried in xdaLineRequest.wells were
        // initially drawn as crosshatch because they weren't in selectedWells.
        // If a filter/groupBy change brings them into mapEntities, promote them
        // to a colored entity so drawShape uses their map color instead of
        // crosshatch. Read from store state directly to avoid stale closure.
        const lineWells = useMapStore.getState().xdaLineRequest?.wells ?? [];
        for (const uwi of lineWells) {
          if (!temp[uwi] && entitiesDict[uwi]) {
            temp[uwi] = {
              color: entitiesDict[uwi].color,
              Uwi: uwi,
              group: entitiesDict[uwi].group ?? ""
            };
          }
        }

        selectedEntities = temp;
      }

      // Line-intersect mode: mapEntities is disabled when selectedWells is empty,
      // so the block above never runs. Use groupModeUwiColors (derived from
      // entitiesOnMap, which refetches on every txnId/filter change) to promote
      // intersecting wells that are now in the filter from crosshatch to their
      // current map color. Wells no longer in the filter simply won't appear here
      // and will fall back to crosshatch in drawShape.
      if (!isGroupMode) {
        const lineWells = useMapStore.getState().xdaLineRequest?.wells ?? [];
        for (const uwi of lineWells) {
          if (!selectedEntities[uwi] && groupModeUwiColors[uwi]) {
            selectedEntities[uwi] = {
              color: groupModeUwiColors[uwi],
              Uwi: uwi,
              group: ""
            };
          }
        }
      }

      if (!activeOverride) {
        handleMeasurementTool(
          measurementDataRef,
          showMeasurement,
          context,
          data,
          activeScreenSize,
          activeLayout,
          zuiRef.current,
          xdaSettings,
          convert,
          getDisplayUnit()
        );
      }

      drawAxis(
        context,
        data,
        activeLayout,
        activeScreenSize,
        xdaSettings,
        settings,
        isScreenshotContext,
        isXdaLineReversed
      );

      if (!activeOverride) {
        shapeRef.current = [];
      }
      if (showIpdb && data.ipdb3d_boxes?.length > 0) {
        // Primary pass: compute colors/stops/legend and cache the result.
        // Screenshot pass: skip all string allocation and read from the cache.
        if (!activeOverride) {
          try {
            const dataAllZeroes = checkDataForFieldIsAllZeroes(data.ipdb3d_boxes);

            let numOfColors = dataAllZeroes
              ? 1
              : Math.ceil((ipdbBin.greaterThan - ipdbBin.lessThan) / ipdbBin.binSize);
            const decimalPlaces = getMaxDecimalPlaces([
              xdaBin?.lessThan,
              xdaBin?.greaterThan,
              xdaBin?.binSize
            ]);
            const stops: [number, string][] = [];
            const hasMaxValue: boolean = ipdbBin?.greaterThan != null;
            const hasMinValue: boolean = !!ipdbBin?.lessThan != null;
            if (hasMinValue) {
              numOfColors += 1;
            }
            if (hasMaxValue) {
              numOfColors += 1;
            }
            let colorMapNumOfColors = numOfColors;
            // Since the number of colors must match the number of bins in the colormap, this is a workaround
            // to allow dynamic bins to function properly. This code can be removed once the colour palette refactor
            // EVA-3858 is complete which should allow colour interpolation between a number of colours
            // that is less than the number of bins
            if (
              xdaSettings.ipdbColor === IpdbColors[0] &&
              numOfColors < IpdbNumOfColorsForPalettes[0]
            ) {
              colorMapNumOfColors = IpdbNumOfColorsForPalettes[0];
            } else if (
              xdaSettings.ipdbColor === IpdbColors[1] &&
              numOfColors < IpdbNumOfColorsForPalettes[1]
            ) {
              colorMapNumOfColors = IpdbNumOfColorsForPalettes[1];
            } else if (
              xdaSettings.ipdbColor === IpdbColors[2] &&
              numOfColors < IpdbNumOfColorsForPalettes[2]
            ) {
              colorMapNumOfColors = IpdbNumOfColorsForPalettes[2];
            } else if (
              xdaSettings.ipdbColor === IpdbColors[3] &&
              numOfColors < IpdbNumOfColorsForPalettes[3]
            ) {
              colorMapNumOfColors = IpdbNumOfColorsForPalettes[3];
            }
            const colors = colormap({
              colormap: xdaSettings.ipdbColor,
              nshades: colorMapNumOfColors,
              format: "hex",
              alpha: 1
            });

            setBinUpdateErrorMessage("");

            if (reverseColor) {
              colors.reverse(); // Reverses in place.
            }

            if (dataAllZeroes) {
              // match color to 3D model if only one color
              stops.push([0, colors[colors.length - 1]]);
            } else {
              for (let i = 0; i < numOfColors; i++) {
                stops.push([ipdbBin.lessThan + ipdbBin.binSize * i, colors[i]]);
              }
            }

            const legendItems = stops.map((stop, i) => {
              // This is to ensure the binValue doesn't exceed the max value if the bin size is not a perfect divisor of the max value.
              const binValue =
                stop[0] > ipdbBin.greaterThan
                  ? ipdbBin.greaterThan.toFixed(decimalPlaces)
                  : stop[0].toFixed(decimalPlaces);
              const li = new LegendItemModel(binValue);
              const previousBinValue =
                i > 0 ? stops[i - 1][0].toFixed(decimalPlaces) : binValue;
              li.text = "";
              if (hasMinValue && i == 0) {
                li.text = "< " + binValue;
              } else if (hasMaxValue && i == numOfColors - 1) {
                li.text = "≥ " + binValue;
              } else {
                li.text = `${previousBinValue} - ${binValue}`;
              }
              li.title = li.text;
              li.value = binValue;
              li.color = stop[1];
              return li;
            });

            //Dispatch to allow for display of default bin settings in options
            if (!xdaBin) {
              setXdaSettings({
                ...xdaSettings,
                ["bin"]: new IpdbBin(
                  ipdbBin.lessThan,
                  ipdbBin.binSize,
                  ipdbBin.greaterThan
                )
              });
            }
            updateIpdbState({
              legendTitle: `${xdaSettings.ipdbField} ${xdaSettings.ipdbUnit}`,
              showIpdb: true,
              legendItems: dataAllZeroes ? [] : legendItems
            });
            // Store for screenshot canvas reuse — zero string allocation on second pass
            ipdbColorCacheRef.current = {
              colors,
              stops,
              numOfColors,
              hasMinValue,
              hasMaxValue
            };
            const drawnIpdbBoxes = drawIpdb3dModel(
              context,
              data,
              activeLayout,
              activeScreenSize,
              xdaSettings,
              (ipdb) => {
                const value = ipdb.val;
                if (value == null) {
                  return { value, color: NullColor };
                }
                if (hasMinValue && value < ipdbBin.lessThan) {
                  return { value, color: colors[0] };
                }
                if (hasMaxValue && value >= ipdbBin.greaterThan) {
                  return { value, color: colors[numOfColors - 1] };
                }
                const index = findFirstGreaterThan(stops, value);
                if (index >= stops.length) {
                  return { value, color: "#fff" };
                }
                if (index < 0) {
                  //last item
                  return { value, color: colors[colors.length - 1] };
                }
                return { value, color: colors[index] };
              },
              ipdbHighlightRectRef
            );
            // Only update the interactive hover ref for the primary canvas
            geoRectanglesRef.current = drawnIpdbBoxes;
          } catch (err) {
            const errorMessage = err?.message ?? "An error occurred.";
            // Change the error message to be more user-friendly.
            const match = errorMessage.match(
              /requires nshades to be at least size (\d+)/
            );
            if (match) {
              const size = match[1];
              setBinUpdateErrorMessage(
                `Selected colour palette requires at least ${size} bins. Please update your bin settings`
              );
            } else {
              setBinUpdateErrorMessage(errorMessage);
            }
          }
        } else if (ipdbColorCacheRef.current) {
          // Screenshot pass: reuse cached colors/stops — zero string allocation.
          const { colors, stops, numOfColors, hasMinValue, hasMaxValue } =
            ipdbColorCacheRef.current;
          drawIpdb3dModel(
            context,
            data,
            activeLayout,
            activeScreenSize,
            xdaSettings,
            (ipdb) => {
              const value = ipdb.val;
              if (value == null) return { value, color: NullColor };
              if (hasMinValue && value < ipdbBin.lessThan)
                return { value, color: colors[0] };
              if (hasMaxValue && value >= ipdbBin.greaterThan)
                return { value, color: colors[numOfColors - 1] };
              const index = findFirstGreaterThan(stops, value);
              if (index >= stops.length) return { value, color: "#fff" };
              if (index < 0) return { value, color: colors[colors.length - 1] };
              return { value, color: colors[index] };
            }
          );
        }
      }
      const disableInteractivity = isScreenshotContext;

      drawZoneLine(
        context,
        data,
        activeLayout,
        activeScreenSize,
        xdaSettings,
        settings,
        isScreenshotContext
      );

      // In group mode selectedWellsAndSticks is always empty, so fall back to
      // checking whether the response actually contains polygon geometry.
      // Also draw when polygons exist but no wells are explicitly selected (line-intersect mode).
      // Filtered-out wells in the API response are skipped inside drawShape before
      // their Two.js path is created, so no ghost polygons appear.
      const hasWellsAndSticks =
        (selectedWellsAndSticks && Object.keys(selectedWellsAndSticks).length !== 0) ||
        data?.polygons?.some((p) => p.length > 0);
      const hasPolygonsNotInList = data.polygons_not_in_list?.length;
      const onlyDrawPolygonNotInListShapes = !!(
        !hasWellsAndSticks && hasPolygonsNotInList
      );
      // Set inside the block below, never read from `activePaths` outside it:
      // that ref is only reset by drawShape, so when drawShape does not run it
      // still holds the PREVIOUS render's paths. Reading it unconditionally
      // would let a stale value decide this render. (EVA-185)
      let drewPolygons = false;
      if (hasWellsAndSticks || hasPolygonsNotInList) {
        drawShape(
          context,
          data,
          activeLayout,
          activeScreenSize,
          {
            ...xdaSettings,
            _isCustomMode: isCustomMode
          } as unknown as typeof xdaSettings,
          selectedEntities,
          onShapeEntered,
          onShapeLeave,
          highlightWells.current,
          activePaths,
          showMeasurement || disableInteractivity,
          showOverlap,
          onlyDrawPolygonNotInListShapes,
          useMapStore.getState().xdaLineRequest?.wells ?? [],
          onShapeClicked
        );
        // Only draw centroids when at least one polygon path was actually rendered.
        // activePaths.current is reset inside drawShape so it reflects only the
        // paths drawn in this call — if all were skipped (e.g. no line intersection)
        // the array stays empty and centroids are suppressed.
        drewPolygons = !onlyDrawPolygonNotInListShapes && activePaths.current.length > 0;
      }

      // Centroids need a polygon to have drawn; dot-wells do not, and Show Data
      // has to label them in a dots-only section too. centroidPassScene decides
      // which rows this pass gets - see its comment. Both sets go through the
      // one call, so their labels share a collision tree rather than landing on
      // each other. (EVA-185)
      const centroidScene = centroidPassScene(data, drewPolygons);
      if (centroidScene) {
        drawCentroid(
          context,
          centroidScene,
          activeLayout,
          activeScreenSize,
          {
            ...xdaSettings,
            _isCustomMode: isCustomMode
          } as unknown as typeof xdaSettings,
          settings,
          isScreenshotContext,
          showData,
          activeOverride ? valueLabelsScreenshotRef : valueLabelsRef
        );
      }

      // EVA-185: dot-only wells are drawn outside the polygon gate above, so a
      // section holding nothing but dots still shows them. Same call for the
      // live and screenshot contexts; `data` is already scaled. No visibility
      // check here — the service returns `duc_wells` only when the toggle is on.
      drawDucWells(context, data, activeLayout, activeScreenSize, {
        ...xdaSettings,
        _isCustomMode: isCustomMode
      } as unknown as typeof xdaSettings);

      // EVA-29: spacing overlay — same draw path for the live and screenshot
      // contexts, so screenshots capture it with no extra plumbing. `data` is
      // already scaled and activeLayout.scale carries the same factor.
      const drewSpacing = drawSpacingOverlay(
        context,
        activeOverride ? spacingScreenshotDataRef : spacingDataRef,
        activeOverride ? valueLabelsScreenshotRef : valueLabelsRef,
        data,
        activeLayout,
        activeScreenSize
      );
      // Only the live pass owns the legend flag; the screenshot pass draws the
      // same anchor into its own registry and must not overwrite it.
      if (!activeOverride) {
        spacingHasLabelsRef.current = drewSpacing;
        // A ref write cannot re-render, so pair it with the sync exactly as
        // the hover/click handlers and the panel effect do. Without this the
        // legend goes stale after any scene render or /line reload: it floats
        // over a plot whose overlay just emptied, and stays hidden when a
        // reload brings neighbours back (hover cannot recover it — that path
        // bails while a well is pinned). syncSpacingSelectionState is a
        // useCallback with [] deps, so it is stable to call from here.
        syncSpacingSelectionState();
      }
    },
    [
      mapEntities?.data,
      mapEntities?.status,
      groupModeUwiColors,
      selectedWells,
      showGrid,
      showIpdb,
      showMeasurement,
      onShapeEntered,
      showOverlap,
      onShapeLeave,
      onShapeClicked,
      drawSpacingOverlay,
      xdaSettings.ipdbColor,
      xdaSettings.ipdbField,
      xdaSettings.includeZero,
      updateIpdbState,
      showData,
      dataFields,
      ipdbSource,
      hangWellsToTop,
      xdaBin,
      reverseColor,
      settings?.topsLabelsFontSize,
      settings?.dataLabelsFontSize,
      settings?.gridLabelsFontSize,
      widthScaled,
      showRelativeDepth,
      showTVDSSDepth,
      xdaLineRequest,
      currentPolygon,
      xdaBin?.isLocked,
      isXdaLineReversed,
      isGroupMode
    ]
  );
  // Keep renderSceneRef (declared above the /line mutation) pointing at the
  // latest renderScene. checkLineVisibilityDebounce is created once via
  // useRef(_debounce(...)) and the mutation's onSuccess closure is frozen at
  // mutate() time, so both would otherwise call a stale renderScene whose color
  // maps (mapEntities/groupModeUwiColors) predate the current render — painting
  // wells as crosshatch. Routing through this ref uses current colors.
  renderSceneRef.current = renderScene;
  useEffect(() => {
    if (zuiRef.current) {
      zuiRef.current.reset();
      zuiRef.current.translateSurface(0, 0);
    }
    if (zuiScreenshotRef.current) {
      zuiScreenshotRef.current.reset();
      zuiScreenshotRef.current.translateSurface(0, 0);
    }
  }, [widthScaled, resetZuiInstance, xdaLineRequest]);

  const checkLineVisibilityDebounce = useRef(
    _debounce((polygon: typeof currentPolygon, lineReq: typeof xdaLineRequest) => {
      if (!polygon || !lineReq.line || lineReq.line.length < 2) {
        setIsLineOutOfView(false);
        return;
      }
      // A lasso filter polygon is not a map viewport — skip the out-of-view check
      if (isLassoFilterPolygon(polygon)) {
        setIsLineOutOfView(false);
        return;
      }
      const outOfView = isXdaOutOfView(
        polygon,
        lineReq.line,
        dataRef.current?.intercept_points
      );
      setIsLineOutOfView(outOfView);
      if (outOfView) {
        two.current.clear();
        removeShapeEventBinding();
        clearMeasurementData(measurementDataRef, two.current, true);
      } else if (dataRef.current && !colorsFetchingRef.current) {
        // Panned back into view — use renderDataRef so we never flash the
        // visualization when no play is selected, and renderSceneRef so we
        // repaint with current well colors (both pierce the stale closure).
        // Skipped while colors are mid-refetch (even the latest closure has
        // empty color maps then); the repaint effects recover it on settle
        // via their colorsFetching dep.
        renderSceneRef.current(renderDataRef.current);
      }
    }, 500)
  ).current;

  useEffect(() => {
    checkLineVisibilityDebounce(currentPolygon, xdaLineRequest);
    return () => checkLineVisibilityDebounce.cancel();
  }, [currentPolygon]);

  //BST to search for first index in a that is greater than b
  function findFirstGreaterThan(a: [number, string][], b: number) {
    let left = 0;
    let right = a.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);

      if (a[mid][0] > b) {
        if (mid === 0 || a[mid - 1][0] <= b) {
          return mid;
        } else {
          right = mid - 1;
        }
      } else {
        left = mid + 1;
      }
    }

    return -1; // Return -1 if no element in 'a' is greater than 'b'
  }

  function removeShapeEventBinding() {
    for (const shape of shapeRef.current) {
      shape.unbind("mouseenter");
      shape.unbind("mouseleave");
    }
    for (const path of pathsRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      path._cleanupListeners?.();
    }
  }

  // EVA-264: `onInteract`, when given, fires after every pan/zoom so the
  // spacing overlay's chips can be recomputed against wherever the viewport
  // now actually is. ZUI only ever transforms `two.scene` directly — it never
  // triggers a React render or redraw on its own — so without this hook a
  // pinned well's chip stays frozen at whatever the canvas box looked like
  // when it was last drawn, drifting out of sync with the well itself as soon
  // as the user pans or zooms. Wired for both instances: the live canvas
  // redraws via `spacingControlsRedraw`, the screenshot canvas via
  // `screenshotSpacingRedraw` (see the two `addZUI` call sites below).
  function addZUI(two, onInteract?: () => void): { zui: ZUI; cleanup: () => void } {
    const domElement = two.renderer.domElement;
    const zui = new ZUI(two.scene, domElement);
    const mouse = new Two.Vector();
    zui.addLimits(0.1, 4);

    function mousedown(e: MouseEvent) {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      window.addEventListener("mousemove", mousemove, false);
      window.addEventListener("mouseup", mouseup, false);
    }
    function mouseup() {
      window.removeEventListener("mousemove", mousemove, false);
      window.removeEventListener("mouseup", mouseup, false);
    }
    function mousemove(e) {
      const dx = e.clientX - mouse.x;
      const dy = e.clientY - mouse.y;
      zui.translateSurface(dx, dy);
      mouse.set(e.clientX, e.clientY);
      onInteract?.();
    }
    function mousewheel(e: WheelEvent) {
      zui.zoomBy(-e.deltaY / 1000, e.clientX, e.clientY);
      onInteract?.();
    }

    domElement.addEventListener("mousedown", mousedown, false);
    domElement.addEventListener("mousewheel", mousewheel, false);
    domElement.addEventListener("wheel", mousewheel, false);

    const cleanup = () => {
      domElement.removeEventListener("mousedown", mousedown, false);
      domElement.removeEventListener("mousewheel", mousewheel, false);
      domElement.removeEventListener("wheel", mousewheel, false);
      window.removeEventListener("mousemove", mousemove, false);
      window.removeEventListener("mouseup", mouseup, false);
    };

    return { zui, cleanup };
  }

  useEffect(() => {
    function setup() {
      if (!sceneRef.current) {
        return;
      }
      const twoContext = two.current;
      // spacingControlsRedraw is declared further down (useRef, created once)
      // but not called until this effect fires, well after that declaration
      // has run — see the EVA-264 comment on addZUI.
      const { zui, cleanup: zuiCleanup } = addZUI(twoContext, () =>
        spacingControlsRedraw()
      );
      zuiRef.current = zui;
      twoContext.appendTo(sceneRef.current);

      const container = sceneRef.current;

      const height = container.clientHeight;
      const width = container.clientWidth;
      screenSize.current = {
        width,
        height
      };
      setParentDimensions({
        width: width,
        height: height
      });

      // Function to check if a point is inside a rectangle
      function isPointInsideRectangle(x, y, rect) {
        const left = rect.translation.x - rect.width / 2;
        const right = rect.translation.x + rect.width / 2;
        const top = rect.translation.y + rect.height / 2;
        const bottom = rect.translation.y - rect.height / 2;
        return x >= left && x <= right && y >= top && y <= bottom;
      }

      function getRectangleUnderMouse(x, y) {
        const rectangles = geoRectanglesRef.current;
        if (!rectangles) {
          return null;
        }
        for (let i = rectangles.length - 1; i >= 0; i--) {
          const rect = rectangles[i];

          if (isPointInsideRectangle(x, y, rect)) {
            return rect;
          }
        }

        return null;
      }

      const onMouseMove = function (event) {
        if (!showXdaValueInfoRef.current || screenshotOverlayVisibleRef.current) {
          return;
        }
        const rectUnderMouse = getRectangleUnderMouse(
          event.offsetX,
          event.offsetY
        ) as Nullable<IpdbCell>;

        if (rectUnderMouse) {
          setValueInfo(rectUnderMouse.value?.toFixed(3) ?? "");
          // drawIpdb3dModel (re)creates ipdbHighlightRectRef.current on every
          // render, in the correct draw-order position (below zone
          // lines/wells/tops) — this handler only ever repositions/shows the
          // existing rect, never creates or re-adds it, so z-order stays
          // correct.
          if (rectUnderMouse.fill && ipdbHighlightRectRef.current) {
            const highlightRect = ipdbHighlightRectRef.current;
            highlightRect.translation.set(
              rectUnderMouse.translation.x,
              rectUnderMouse.translation.y
            );
            highlightRect.width = rectUnderMouse.width;
            highlightRect.height = rectUnderMouse.height;
            highlightRect.fill = lightenColor(rectUnderMouse.fill, 0.6);
            highlightRect.visible = true;
          }
        }
      };

      const onMouseOut = function () {
        if (screenshotOverlayVisibleRef.current) {
          return;
        }
        if (ipdbHighlightRectRef.current) {
          ipdbHighlightRectRef.current.visible = false;
        }
      };

      two.current.renderer.domElement.addEventListener("mousemove", onMouseMove);
      two.current.renderer.domElement.addEventListener("mouseout", onMouseOut);

      return () => {
        two.current.renderer?.domElement.removeEventListener("mousemove", onMouseMove);
        two.current.renderer?.domElement?.removeEventListener("mouseout", onMouseOut);
        zuiCleanup();
        removeShapeEventBinding();
        two.current.clear();
        // two.js pins every `new Two(...)` instance forever in its
        // session-global Two.Instances array, so this Two context is never
        // garbage collected on unmount — the ipdb grid's cached
        // canvas/texture/sprite (and its Texture.ImageRegistry entry) would
        // otherwise leak on every XdaViewer mount/unmount cycle.
        releaseIpdbGridResources(two.current);
      };
    }

    return setup();
  }, []);

  // Mount the screenshot instance lazily — only on the first time screenshot mode opens.
  // This avoids allocating a second Two.js instance, canvas, and ZUI on every component mount.
  useEffect(() => {
    if (!screenshotMounted || !sceneScreenshotRef.current) return;
    const t = new Two(params);
    t.appendTo(sceneScreenshotRef.current);
    t.width = screenshotBounds.width;
    t.height = screenshotBounds.height;
    screenshotScreenSizeRef.current = {
      width: screenshotBounds.width,
      height: screenshotBounds.height
    };
    twoScreenshot.current = t;

    const { zui, cleanup: zuiCleanup } = addZUI(t, () => screenshotSpacingRedraw());
    zuiScreenshotRef.current = zui;

    if (dataRef.current) {
      renderToScreenshotCanvas(dataRef.current);
    }

    return () => {
      zuiCleanup();
      t.clear();
      // Same leak as the main setup-effect cleanup above — this screenshot
      // Two context is also pinned forever in Two.Instances, so its ipdb grid
      // cache/registry entry must be released explicitly. Uses the closed-over
      // `t` rather than `twoScreenshot.current` since that ref is nulled out
      // on the very next line.
      releaseIpdbGridResources(t);
      twoScreenshot.current = null;
      zuiScreenshotRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshotMounted]);

  useEffect(() => {
    if (!twoScreenshot.current) return;
    twoScreenshot.current.width = screenshotBounds.width;
    twoScreenshot.current.height = screenshotBounds.height;
    screenshotScreenSizeRef.current = {
      width: screenshotBounds.width,
      height: screenshotBounds.height
    };
    const isPresetChange = selectedPreset?.id !== prevPresetIdRef.current;
    prevPresetIdRef.current = selectedPreset?.id;
    if (isPresetChange && zuiScreenshotRef.current) {
      zuiScreenshotRef.current.reset();
      zuiScreenshotRef.current.translateSurface(0, 0);
    }
    if (dataRef.current) {
      renderToScreenshotCanvas(dataRef.current);
    }
  }, [screenshotBounds.width, screenshotBounds.height]);

  useEffect(() => {
    if (!screenshotOverlayVisible) return;
    // Ensure no stale hover UI leaks into screenshot mode.
    ReactTooltip.hide(labelRef.current);
    setWellLabel("");
    setAttentionWells([]);
    setValueInfo("");
    if (ipdbHighlightRectRef.current) {
      ipdbHighlightRectRef.current.visible = false;
    }
  }, [screenshotOverlayVisible, setAttentionWells]);

  const resizeDebounce = _debounce((client) => {
    const width = client.contentRect.width;
    const height = client.contentRect.height;
    // Skip resize when the primary canvas is hidden
    if (width === 0 || height === 0) return;
    screenSize.current = {
      width,
      height
    };
    setParentDimensions({
      //adding 30 to account for the 30px of padding-right in sceneWrapper
      width: width + 30,
      height: height
    });
    two.current.width = width;
    two.current.height = height;
    // Dimensions above are always applied; the paint is skipped while colors
    // are mid-refetch (it would flash empty coloring). The repaint effects
    // recover it on settle via their colorsFetching dep. Read through the ref:
    // this debounced callback can fire after the render that created it.
    if (colorsFetchingRef.current) return;
    renderScene(isXdaPending || awaitingPlaySelection ? emptyScene : dataRef.current);
  }, 300);

  useResizeObserver(sceneRef, (client) => resizeDebounce(client));

  const requestDebounce = _debounce(async () => {
    if (!xdaLineRequest.line.length || !topsModelSource.length) return;
    const line = {
      start: {
        x: xdaLineRequest.line[0][0],
        y: xdaLineRequest.line[0][1]
      },
      end: {
        x: xdaLineRequest.line[1][0],
        y: xdaLineRequest.line[1][1]
      }
    };
    // Prefer explicit map selections, but fall back to the wells intersecting
    // the drawn XDA line. In group mode the rendered features are synthetic
    // group entities, so selectedWells can legitimately be empty while the line
    // request still has real UWIs from /map/wells-intersect. A host that owns
    // its section has no selection to prefer, so it gets the line's wells.
    const wells = getXdaPayloadWells(sectionSelection, xdaLineRequest.wells);
    mutate({ line, wells, dataFields, groupMode: isGroupMode });
  }, 500);

  useEffect(() => {
    highlightWells.current = attentionWells;
    for (const path of pathsRef.current) {
      if (attentionWells.includes(path.uwi)) {
        path.fill = path.stroke;
      } else if (showOverlap) {
        path.fill = path.defaultFill ?? convertHexToRGBA(path.stroke, 0.3);
      } else {
        path.fill = path.defaultFill ?? "rgba(0,0,0,0)";
      }
    }
    //renderScene(dataRef.current);
  }, [attentionWells, renderScene, showOverlap]);

  useEffect(() => {
    // A new request is about to fire — mark pending so stale play warnings from
    // the previous line are suppressed until the fresh response arrives.
    // Also clear renderDataRef synchronously so effects later in this same cycle
    // (e.g. visual-changes) read emptyScene and don't re-paint the stale scene.
    if (xdaLineRequest.line.length) {
      setIsXdaPending(true);
      renderDataRef.current = emptyScene;
    }
    requestDebounce();
    return () => requestDebounce.cancel();
    // playConstraint and mapEntities?.data are intentionally excluded: neither is
    // part of the /line payload. They are React Query results that get a new
    // reference whenever their query refetches (e.g. on a filter/groupBy/txnId
    // change), so including them fired redundant requests with an identical body.
    // playConstraint's only effect — setting up/down/left/right dimensions — already
    // re-triggers this effect via those deps. mapEntities?.data only affects well
    // colors and is handled by the visual-changes render effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    xdaLineRequest,
    upHeight,
    downHeight,
    leftWidth,
    rightWidth,
    showCompletion,
    scaleByOption,
    topsModelSource,
    selectedPlays,
    scaleByValue,
    completionLength,
    hangWellsToTop,
    showAllTops,
    showOverlap,
    showDucWells,
    xdaBin?.isLocked,
    checkedPadScenariosWithSticks,
    sectionSelection,
    isGroupMode,
    dataFields,
    ipdbSource,
    showIpdb,
    xdaSettings.ipdbField
  ]);

  // Re-render the canvas for visual changes that dont require data fetch
  useEffect(() => {
    if (!dataRef.current) return;
    // Skip while a /line request is in flight — renderDataRef.current is
    // emptyScene during that window, so painting here (e.g. colors settling
    // mid-flight) would blank the canvas. onSuccess paints when it resolves.
    if (isXdaPending) return;
    // Skip while well colors are mid-refetch — painting now would flash the
    // empty/default coloring. colorsFetching is a dep, so this effect re-fires
    // when the gate clears even if no data reference changed (e.g. a color
    // query errored and settled with the same data).
    if (colorsFetching) return;
    renderScene(renderDataRef.current).then(() => {
      // Also repaint the screenshot canvas: when the overlay is open the main
      // canvas is hidden, and a paint skipped by onSuccess (colors mid-refetch)
      // is recovered here — without this the exported image would stay stale.
      if (screenshotOverlayVisibleRef.current)
        renderToScreenshotCanvas(renderDataRef.current);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showMeasurement,
    showGrid,
    showData,
    showTooltip,
    showIpdb,
    reverseColor,
    showRelativeDepth,
    showTVDSSDepth,
    xdaSettings.ipdbColor,
    xdaSettings.ipdbField,
    xdaSettings.includeZero,
    widthScaled,
    // Repaint both canvases when a screenshot label size changes so the preview
    // updates live (without these the change only applied on an unrelated repaint,
    // e.g. editing Width — see EVA-6864).
    settings?.topsLabelsFontSize,
    settings?.dataLabelsFontSize,
    settings?.gridLabelsFontSize,
    xdaBin,
    selectedPlays,
    noWellsAndSticks,
    // Repaint (not refetch) when well colors change after a filter/groupBy change.
    // renderScene captures mapEntities in its closure, so the new colors apply
    // immediately without another /line request.
    mapEntities?.data,
    // Re-fire when the mid-refetch gate above clears, so a skipped paint is
    // recovered even when the color data reference did not change.
    colorsFetching
  ]);

  // Latest renderToScreenshotCanvas / redrawSpacingOverlayNow, for the
  // debounced repaints below. Each debounce is created ONCE, so calling either
  // function directly would pin the first render's closure forever.
  const renderToScreenshotCanvasRef = useRef<(data: XdaData) => Promise<void>>();
  renderToScreenshotCanvasRef.current = renderToScreenshotCanvas;
  const redrawSpacingOverlayNowRef = useRef<() => void>();
  redrawSpacingOverlayNowRef.current = redrawSpacingOverlayNow;
  const syncSpacingSelectionStateRef = useRef<() => void>();
  syncSpacingSelectionStateRef.current = syncSpacingSelectionState;

  // EVA-163: debounced LIVE redraw for the spacing controls. All six numeric
  // inputs commit on change rather than on blur, so typing "1500" fires this
  // effect four times — and the live path is NOT cheap: chip measurement goes
  // through `calculateTextDimensions`, which appends a span, reads
  // clientWidth and removes it, i.e. one forced synchronous layout per chip.
  // On a dense cross-section that is hundreds of layout flushes per field
  // entry. 80ms coalesces a typist's keystrokes while staying under the
  // threshold where as-you-type feedback starts to feel detached.
  const spacingControlsRedraw = useRef(
    _debounce(() => {
      redrawSpacingOverlayNowRef.current?.();
      syncSpacingSelectionStateRef.current?.();
    }, 80)
  ).current;

  // EVA-163: debounced screenshot repaint for the spacing controls. Created
  // once so successive keystrokes coalesce into a single scene render.
  const screenshotSpacingRedraw = useRef(
    _debounce(() => {
      if (screenshotOverlayVisibleRef.current) {
        renderToScreenshotCanvasRef.current?.(renderDataRef.current);
      }
    }, 400)
  ).current;

  // Both spacing debounces are created once and persist for the widget's life,
  // so they have to be cancelled or a pending redraw fires into a torn-down
  // Two.js scene.
  useEffect(
    () => () => {
      spacingControlsRedraw.cancel();
      screenshotSpacingRedraw.cancel();
    },
    [spacingControlsRedraw, screenshotSpacingRedraw]
  );

  // EVA-163: the Spacing Class controls change ONLY the spacing overlay — the
  // octant parameters and the class filter never touch well shapes, tops, the
  // geo grid or the layout. So they redraw that overlay directly instead of
  // joining the visual-changes list above, which repaints every node in the
  // scene; these are popover controls a user tweaks repeatedly, and a full
  // rebuild per keystroke is the EVA-6911 cost profile.
  //
  // Nothing happens unless a well is anchored: with no pin and no hover
  // `redrawSpacingOverlayNow` just clears, which is already the case.
  useEffect(() => {
    if (!dataRef.current) return;
    // Debounced, and the sync rides along inside it — see spacingControlsRedraw.
    spacingControlsRedraw();
    // The pairing is the same one the hover/click handlers use:
    // the redraw writes `spacingHasLabelsRef`, which is a ref and cannot
    // re-render, so without this the legend goes stale in BOTH directions —
    // it lingers beside a plot the filters just emptied, and stays hidden
    // after a cap is widened until the next hover.
    //
    // The screenshot canvas draws the overlay from the same settings, so keep
    // an open preview in step (matching the visual-changes effect above).
    // BOTH canvases are debounced, at different delays: the live one at 80ms
    // so as-you-type feedback stays attached, the screenshot one at 400ms
    // because a full scene render per digit is the EVA-6911 cost profile this
    // effect exists to avoid, merely relocated to the other canvas.
    if (screenshotOverlayVisibleRef.current) {
      screenshotSpacingRedraw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spacingClassFilter,
    octantLengthOverrides,
    octantHorCutoffDeg,
    octantVertCutoffDeg
  ]);

  // In group mode, selectedWells is always empty so mapEntities is disabled and
  // never changes. Colors come from groupModeUwiColors (derived from entitiesOnMap
  // which refetches on every txnId change). When groupModeUwiColors updates we must
  // explicitly re-render the cached scene so the new colors are applied.
  // Also used in line-intersect mode (no selectedWells) to repaint crosshatch wells
  // that become available after a filter/groupBy change.
  // renderScene is intentionally excluded: it is already recreated with the new
  // groupModeUwiColors value by the time this effect fires, so the call below
  // captures the correct closure. Including it would cause an extra render on
  // every unrelated renderScene dep change.
  useEffect(() => {
    if (!dataRef.current) return;
    // Skip while colors are mid-refetch (see visual-changes effect above).
    if (colorsFetching) return;
    const renderData =
      isXdaPending || awaitingPlaySelection ? emptyScene : dataRef.current;
    renderScene(renderData).then(() => {
      // Keep the screenshot canvas in sync when the overlay is open — it is
      // the only visible canvas then (see visual-changes effect above).
      if (screenshotOverlayVisibleRef.current) renderToScreenshotCanvas(renderData);
    });
    // colorsFetching is a dep so the false transition itself recovers a paint
    // skipped above, even when groupModeUwiColors did not change reference
    // (e.g. the entitiesOnMap query errored and settled with the same data).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupModeUwiColors, colorsFetching]);

  // lock map before going fullscreen
  const handleFullscreenToggle = (value) => {
    dashboardDispatch({ payload: { lockMap: value } });
  };

  // Both hops of the fullscreen chain (screenshotOverlayVisible → lockMap →
  // onFullscreenToggle) must flush before the browser paints, or the viewer's
  // screenshot CSS reverts a frame ahead of the widget collapsing and the XDA
  // content flashes fullscreen (EVA-6911). useLayoutEffect state updates are
  // flushed synchronously pre-paint in React 17 legacy mode.
  useLayoutEffect(() => {
    onFullscreenToggle(lockMap);
  }, [lockMap]);

  useLayoutEffect(() => {
    handleFullscreenToggle(screenshotOverlayVisible);
  }, [screenshotOverlayVisible]);

  useEffect(() => {
    if (!dataRef.current) return;
    if (screenshotOverlayVisible) {
      if (!screenshotMounted) {
        setScreenshotMounted(true);
        return;
      }
      renderToScreenshotCanvas(
        isXdaPending || awaitingPlaySelection ? emptyScene : dataRef.current
      );
    } else {
      twoScreenshot.current?.clear();
      pathsScreenshotRef.current = [];
      renderScene(isXdaPending || awaitingPlaySelection ? emptyScene : dataRef.current);
    }
    // renderScene is intentionally excluded: we only want this effect to fire when
    // screenshotOverlayVisible changes. renderScene is stable enough via its own
    // useCallback, and including it here caused a re-render cascade on every
    // settings change (renderScene ref changes → effect fires → extra full redraw).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshotOverlayVisible, screenshotMounted]);

  useEffect(() => {
    // EVA-3780 snapshot compatibility: the flat store is reconstructed into the
    // legacy `{xda:{settings:...}}}` nested shape only at this sessionStorage
    // serialization boundary.
    //
    // The two sources are left out until the user's settings read has answered,
    // the same rule the 3D viewer follows. Until then they hold a fallback, and
    // the settings provider treats a `topsModelSource` that is merely present as
    // a saved choice — so writing one now makes its apply-the-user-default
    // effect return early for the rest of the session. That is exactly why the
    // well card's seed omits these two fields, and a session payload written a
    // moment later would otherwise put them straight back.
    const sessionPayload = {
      xda: {
        // Restored state needs no wait. A dashboard widget rehydrated from a
        // saved componentState carries an explicit `topsModelSource`, and that
        // is a choice somebody made — held back, its own dashboard save would
        // read a payload without it. The card's viewer is the other case: its
        // seed omits both fields on purpose, so nothing was chosen and the
        // wait applies. Only the provider sees which of the two this is, so it
        // publishes the answer rather than the viewer guessing.
        //
        // What the wait is on is the shared settings query. Wherever anything
        // on the page has it fetching — which is the ordinary case, since many
        // callers subscribe to that key — it settles either way, success or
        // failure, and the next write carries the two fields. On a surface
        // where nothing has enabled it the query stays idle, and they are
        // simply never written for that session. That costs nothing: with no
        // source in storage the settings provider fills both from the user's
        // own defaults, which is the answer this is protecting in the first
        // place.
        settings: buildXdaPersistedSettings(
          xdaSettings,
          hasRestoredSources,
          haveUserSettingsAnswered
        )
      }
    };
    const newSettings = JSON.stringify(sessionPayload);
    // Update the session storage.
    sessionStorage.setItem(sessionKey, newSettings);
  }, [xdaSettings, haveUserSettingsAnswered, hasRestoredSources, sessionKey]);

  // Clear stale error + canvas when tops are deselected so they don't persist on re-selection
  useEffect(() => {
    if (!topsModelSource.length) {
      resetMutation();
      // EVA-29: the scene is about to empty — release the spacing selection.
      selectedEntryIndexRef.current = null;
      hoveredEntryIndexRef.current = null;
      syncSpacingSelectionState();
      renderScene(emptyScene);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topsModelSource]);

  // Computed at render time so the warning gate is in sync for this frame.
  // In Strict Mode's dev double-invoke, the second pass sees playsJustArrived=false;
  // the one-frame consequence (a brief dev-only warning flash) is acceptable.
  const _warningPlaysKey = availablePlays.join(",");
  const playsJustArrived = _warningPlaysKey !== prevWarningPlaysKeyRef.current;
  prevWarningPlaysKeyRef.current = _warningPlaysKey;

  let warningMessage: string | null = null;
  if (!topsModelSource.length) {
    warningMessage = "No tops model source selected.";
  } else if (!xdaLineRequest?.line?.length && !checkedPadScenarios.length) {
    warningMessage =
      "No lines drawn. Please use line tool on the map toolbar to draw a cross section.";
  } else if (isLineOutOfView) {
    warningMessage =
      "Cross section line is outside the map view. Pan back to the line or draw a new one.";
  } else if (
    noWellsAndSticks &&
    availablePlays.length > 0 &&
    selectedPlays.length === 0 &&
    !isXdaPending &&
    !(availablePlays.length === 1 && playsJustArrived)
  ) {
    warningMessage =
      "Select a play from the Selected Plays dropdown to display the visualization.";
  } else if (
    noWellsAndSticks &&
    availablePlays.length === 0 &&
    hasFetchedRef.current &&
    !isLoading &&
    !isXdaPending
  ) {
    warningMessage = "No surface sources available for this location.";
  }

  const xdaToolbar = (
    <XdaToolbar
      mouseoverRef={mouseoverRef}
      binUpdateErrorMessage={binUpdateErrorMessage}
      onFullscreenToggle={onFullscreenToggle}
      hideFullscreen={hideFullscreen}
      embedded={!!toolbarContainer}
      hostElement={toolbarContainer}
      screenshotWidgetId={screenshotWidgetId}
      availablePlays={availablePlays}
      isLoading={isLoading}
      noWellsAndSticks={noWellsAndSticks}
      selectedWells={selectedWells}
      ducWells={ducUwisInScene}
    />
  );

  /*
   * Only the DOM position moves. The toolbar stays in this component's React
   * tree, so the settings store, the screenshot context and every toggle's
   * own provider still reach it through the portal.
   */
  const hostToolbar = (toolbar: JSX.Element) =>
    toolbarContainer ? createPortal(toolbar, toolbarContainer) : toolbar;

  return (
    <Wrapper
      ref={mouseoverRef}
      className="xda-viewer"
      data-testid={`widget-${widgetType}`}
      isScreenshot={screenshotOverlayVisible}>
      <Screenshot
        key="screenshot"
        containerId={screenshotContainerId}
        screenshotWidgetId={screenshotWidgetId}
      />

      <XDAContainer
        className="xda-viewer-container"
        isScreenshot={screenshotOverlayVisible}
        screenshotWidth={screenshotBounds.width}
        screenshotHeight={screenshotBounds.height}>
        <ScreenshotContainer id={screenshotContainerId} />
        {/* 2 canvases so we dont need to resize when going in and out of screenshot */}
        <SceneWrapper
          ref={sceneRef}
          onContextMenu={(e) => e.preventDefault()}
          showMeasurement={showMeasurement}
          allowPointerEvents={!screenshotOverlayVisible}
          isHidden={screenshotOverlayVisible}
        />
        {/* Screenshot canvas — rendered lazily on first open, then kept alive (hidden) across toggles */}
        {screenshotMounted && (
          <SceneWrapper
            ref={sceneScreenshotRef}
            onContextMenu={(e) => e.preventDefault()}
            showMeasurement={false}
            allowPointerEvents={true}
            isHidden={!screenshotOverlayVisible}
          />
        )}

        {(isLoading || isRendering) && (
          <BusyOverlay onContextMenu={(e) => e.preventDefault()}>
            <IconSpinner />
          </BusyOverlay>
        )}
        {isError && topsModelSource.length > 0 && (
          <ErrorOverlay>
            <Alert type="error" message={`Unable to generate XDA, ${errorMsg}.`} />
          </ErrorOverlay>
        )}
        {showXdaValueInfo && (
          <ValueInformationContainer>{valueInfo}</ValueInformationContainer>
        )}
        {(!isError || !topsModelSource.length) && warningMessage && (
          <WarningOverlay>
            <Alert type="warning" message={warningMessage} />
          </WarningOverlay>
        )}

        {xdaSettings.showIpdb && activeIpdbLegend && (
          <IpdbLegend
            height={250}
            width={180}
            parentDimensions={
              screenshotOverlayVisible
                ? { width: screenshotBounds.width, height: screenshotBounds.height }
                : parentDimensions
            }
            ipdbTitleSize={activeIpdbTitleSize}
            ipdbFontSize={activeIpdbFontSize}
            backgroundOpacity={activeBackgroundOpacity}
            showLegendBorder={activeShowLegendBorder}
            location="xda"
          />
        )}
        {/* EVA-29: octant zone legend — mounted only while a well is PINNED
            (never on hover, gone once nothing is selected), as a direct child
            of XDAContainer so react-rnd's bounds="parent" is the viewer, no
            extra layout box is added, and snapdom captures it in screenshot
            mode. */}
        {/* PARKED (EVA-163 comment 163139) — the octant zone legend.
            Removed from the widget AND the screenshot; the labels are
            black now, so there are no zone colours left for it to key.

        {activeSpacingLegendVisible && spacingLegendHasContent && (
          <XdaSpacingLegend
            storageScope={sessionKey}
            zones={visibleSpacingZones}
            fontSize={activeIpdbFontSize}
            showLegendBorder={activeSpacingLegendBorder}
            backgroundOpacity={activeSpacingLegendOpacity}
            parentDimensions={
              screenshotOverlayVisible
                ? { width: screenshotBounds.width, height: screenshotBounds.height }
                : parentDimensions
            }
          />
        )}
        */}
      </XDAContainer>

      {!screenshotOverlayVisible && hostToolbar(xdaToolbar)}

      <Label
        ref={labelRef}
        top={labelTop}
        left={labelLeft}
        data-tip={wellLabel}
        data-for={tooltipId}
      />
      <ReactTooltip id={tooltipId} html={true} />
    </Wrapper>
  );
}

export default XdaViewer;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: inherit;

  ${(props) =>
    props.isScreenshot &&
    `
    display: grid;
    background-color: rgba(46, 72, 88, 0.24);
  `}
`;

const XDAContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: inherit;

  ${(props) =>
    props.isScreenshot &&
    `
    justify-self: center;
    width: ${props.screenshotWidth}px;
    height: ${props.screenshotHeight}px;
    background-color: white;
    top: 60px;
  `}
`;

// Warning-only overlay. A warning can now sit on top of a scene that is still
// drawn (the cross-section frame with no surface sources), so unlike the shared
// error overlay it must not swallow pointer events meant for the canvas below it.
const WarningOverlay = styled.div`
  position: absolute;
  left: 2px;
  top: 2px;
  right: 0;
  bottom: 0;
  background: #a8a8a811;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 997;
`;

const ValueInformationContainer = styled.div`
  display: flex;
  height: 30px;
  position: absolute;
  bottom: 2px;
  width: 100%;
  justify-content: center;
`;

const SceneWrapper = styled.div`
  width: 100%;
  height: 100%;
  cursor: ${(p) => (p.showMeasurement ? "crosshair" : "default")};
  pointer-events: ${(p) =>
    p.isHidden || p.allowPointerEvents === false ? "none" : "auto"};
  ${(p) => p.isHidden && "display: none;"}

  text {
    user-select: none;
  }
`;

const ScreenshotContainer = styled.div`
  position: absolute;
  display: grid;
  justify-content: center;
  pointer-events: none;
`;

const Label = styled.span`
  position: absolute;
  left: ${(p) => p.left}px;
  top: ${(p) => p.top}px;
`;
